import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { openCalendlyDemo } from "@/lib/analytics";

interface CustomerCard {
  slug: string;
  name: string;
  industry: string;
  erp: string;
  headline: string;
  headlineStats: { label: string; value: string }[];
}

const customers: CustomerCard[] = [
  {
    slug: 'health-system-oracle-fusion',
    name: 'Major Australian health service',
    industry: 'Healthcare',
    erp: 'Oracle Fusion Cloud ERP',
    headline:
      'Replaced manual AP triage with AI email exception prevention on Oracle Fusion — 2,500+ supplier emails processed with zero errors in a proof of concept.',
    headlineStats: [
      { label: 'Emails processed', value: '2,500+' },
      { label: 'Processing errors', value: '0' },
      { label: 'Invoice scenarios', value: '15' },
    ],
  },
  {
    slug: 'school-district-oracle-ebs',
    name: 'Large US public school district',
    industry: 'K-12 Education',
    erp: 'Oracle E-Business Suite',
    headline:
      'Replaced manual AP with EZ Cloud on Oracle EBS — live in production across ~25 schools and departments, with fund accounting, district tax, and in-workflow receiving.',
    headlineStats: [
      { label: 'In production', value: 'Live' },
      { label: 'Schools & departments', value: '~25' },
      { label: 'Users onboarded', value: '~48' },
    ],
  },
  {
    slug: 'hacov',
    name: 'Housing Authority of Covington',
    industry: 'Public Housing / Property Management',
    erp: 'Yardi Voyager',
    headline:
      'Cut AP processing time from 7 days to 2 and saved $79K/year with a seamless Yardi Voyager integration.',
    headlineStats: [
      { label: 'Faster processing', value: '71%' },
      { label: 'Fewer errors', value: '80%' },
      { label: 'Annual savings', value: '$79K+' },
    ],
  },
];

const Customers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'EZ Cloud Customer Stories',
    description:
      'Real outcomes from EZ Cloud customers — AP automation case studies across property management, healthcare, manufacturing, and more.',
    url: 'https://ezcloud.ai/customers',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: customers.map((c, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://ezcloud.ai/customers/${c.slug}`,
        name: `${c.name} — ${c.industry}`,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Customer Stories | EZ Cloud AP Automation"
        description="Real results from EZ Cloud customers across property management, healthcare, manufacturing, and more. See how finance teams cut AP processing time and costs."
        canonical="https://ezcloud.ai/customers"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Customers', url: 'https://ezcloud.ai/customers' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main className="container mx-auto px-4 py-16 max-w-6xl">
        <header className="mb-12 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Customer Stories
          </h1>
          <p className="text-lg text-gray-600">
            Finance teams across property management, healthcare, manufacturing, and public-sector organizations rely on EZ Cloud to transform their accounts payable operations. Here's how.
          </p>
        </header>

        <section aria-labelledby="stories" className="space-y-6">
          <h2 id="stories" className="sr-only">
            All case studies
          </h2>

          {customers.map((c) => (
            <Card
              key={c.slug}
              className="p-8 bg-white border border-gray-200 shadow-sm hover:bg-white/[0.07] transition-colors"
            >
              <div className="grid md:grid-cols-[auto,1fr,auto] gap-6 items-start">
                <div className="w-14 h-14 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-7 h-7 text-accent" aria-hidden />
                </div>

                <div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-2">
                    <span>{c.industry}</span>
                    <span aria-hidden>·</span>
                    <span>ERP: {c.erp}</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                    {c.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{c.headline}</p>
                  <div className="flex flex-wrap gap-6 text-sm">
                    {c.headlineStats.map((s) => (
                      <div key={s.label}>
                        <span className="text-2xl font-bold text-accent mr-2">
                          {s.value}
                        </span>
                        <span className="text-gray-600">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button variant="outline" asChild className="self-center">
                  <Link to={`/customers/${c.slug}`}>
                    Read story
                    <ArrowRight className="ml-2 w-4 h-4" aria-hidden />
                  </Link>
                </Button>
              </div>
            </Card>
          ))}
        </section>

        {/* Industries we serve */}
        <section aria-labelledby="industries-served" className="mt-20">
          <h2 id="industries-served" className="text-2xl font-semibold text-gray-900 mb-6">
            Industries we serve
          </h2>
          <p className="text-gray-600 mb-8 max-w-3xl">
            EZ Cloud customers span six core industries. Each has its own AP-automation patterns and approval workflows — pick the one closest to your business to see the full solution.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/industries/healthcare"
              className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
            >
              <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                Healthcare
                <ArrowRight className="w-4 h-4" aria-hidden />
              </div>
              <p className="text-sm text-gray-600">
                HIPAA-compliant invoice automation, native Oracle integration, AI exception resolution.
              </p>
            </Link>
            <Link
              to="/industries/property-management"
              className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
            >
              <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                Property Management
                <ArrowRight className="w-4 h-4" aria-hidden />
              </div>
              <p className="text-sm text-gray-600">
                Yardi-native AP, multi-property GL coding, contractor insurance certificates handled.
              </p>
            </Link>
            <Link
              to="/industries/manufacturing"
              className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
            >
              <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                Manufacturing
                <ArrowRight className="w-4 h-4" aria-hidden />
              </div>
              <p className="text-sm text-gray-600">
                3-way match, GRN handling, dimensional posting into SAP, Oracle, NetSuite, Dynamics.
              </p>
            </Link>
            <Link
              to="/industries/education"
              className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
            >
              <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                Education
                <ArrowRight className="w-4 h-4" aria-hidden />
              </div>
              <p className="text-sm text-gray-600">
                K-12 and higher-ed AP — school-year budget cycles, fund accounting, grant tracking.
              </p>
            </Link>
          </div>
        </section>

        <section className="mt-16 text-center border-t border-gray-200 pt-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            Want to be our next customer story?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Book a 20-minute demo and we'll walk through your specific AP workflow. If your team runs Oracle, NetSuite, SAP, Dynamics, or Yardi — we integrate natively.
          </p>
          <Button
            size="lg"
            variant="accent"
            onClick={() =>
              openCalendlyDemo()
            }
          >
            Book a demo
            <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
          </Button>
        </section>
      </main>
    </div>
  );
};

export default Customers;
